import type { TelephonyConfig } from '../lib/types';

/**
 * The media layer, behind one interface.
 *
 * A CAD call has two halves. The *record* — who called, when, what came of it —
 * lives in Postgres and is driven by the REST API. The *media* — the actual
 * audio path — is what this interface covers. Keeping them apart is what lets
 * the same console run against a real SIP trunk in production and against the
 * simulator in the sandbox: only the media half changes, and every panel, state
 * machine and permission check above it stays identical.
 *
 * Nothing outside `telephony/` may import a SIP library or branch on the driver
 * name. If that rule breaks, the abstraction has leaked.
 */

export type RegistrationState = 'idle' | 'connecting' | 'registered' | 'failed';

export interface MediaCall {
  /** SIP Call-ID, used to correlate the media leg with the CAD call record. */
  sipCallId: string;
  direction: 'inbound' | 'outbound';
  /** Remote party as the trunk presented it. */
  remoteIdentity: string;
  displayName: string | null;
  establishedAt: number | null;
}

export interface DriverEvents {
  /** Registration state changed — surfaced in the softphone header. */
  onRegistration: (state: RegistrationState, detail?: string) => void;
  /** An inbound leg is ringing at this extension. */
  onIncoming: (call: MediaCall) => void;
  /** The media path came up. */
  onEstablished: (call: MediaCall) => void;
  /** The far end (or we) hung up. */
  onEnded: (sipCallId: string, reason: string) => void;
  /** A remote audio stream is available, for recording and the audio element. */
  onRemoteStream: (sipCallId: string, stream: MediaStream) => void;
}

export interface TelephonyDriver {
  readonly name: 'sip' | 'mock' | 'webrtc';
  /** True when this driver moves real audio; false for the simulator. */
  readonly hasMedia: boolean;

  start(config: TelephonyConfig, events: DriverEvents): Promise<void>;
  stop(): Promise<void>;

  /** Accepts the ringing leg matching `sipCallId`, or the only one ringing. */
  answer(sipCallId?: string | null): Promise<void>;
  hangup(sipCallId?: string | null): Promise<void>;
  hold(sipCallId: string | null, held: boolean): Promise<void>;
  mute(muted: boolean): Promise<void>;
  sendDtmf(sipCallId: string | null, digits: string): Promise<void>;

  /** Places an outbound leg; resolves with the SIP Call-ID it was given. */
  dial(destination: string): Promise<string>;

  /** Blind transfer (REFER) to an extension or number. */
  transfer(sipCallId: string | null, target: string): Promise<void>;

  /** Streams for recording. Empty when the driver carries no media. */
  streams(sipCallId?: string | null): MediaStream[];
}

/** Digits an agent may send; anything else is rejected before it reaches SIP. */
export const DTMF_PATTERN = /^[0-9*#]+$/;

export function assertDtmf(digits: string): void {
  if (!DTMF_PATTERN.test(digits)) throw new Error(`Invalid DTMF sequence: ${digits}`);
}
